import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'

// Snapshot fields that carry human-readable copy, in priority order. Used to
// build the "Summary" column for non custom_html widgets, which keep their
// markup inside `variables` rather than in `content`.
const SUMMARY_VARIABLE_KEYS = ['text', 'buttonText', 'linkUrl']
const SUMMARY_MAX_LENGTH = 60

export default {
  name: 'setting-widgets-history',
  props: {
    widgetId: {
      type: [String, Number],
      required: true
    }
  },
  data () {
    return {
      // Id of the version whose Restore button is currently armed (showing
      // "Confirm?"). Only one row can be armed at a time.
      confirmingId: null,
      // Id of the version being restored right now, drives the button spinner.
      restoringId: null
    }
  },
  computed: {
    ...mapGetters({
      versions: 'settingWidgets/versions',
      isVersionsLoading: 'settingWidgets/isVersionsLoading',
      isLoading: 'settingWidgets/isLoading'
    })
  },
  mounted () {
    this.loadVersions()
  },
  methods: {
    ...mapActions({
      fetchVersions: 'settingWidgets/VERSIONS_LIST',
      restoreVersion: 'settingWidgets/VERSION_RESTORE'
    }),
    loadVersions () {
      if (!this.widgetId) return
      this.fetchVersions(this.widgetId)
    },
    // Backend sends ISO timestamps; render them in the same Y-m-d H:i format
    // the GENERAL editor emits, so operators see one consistent date style.
    formatDate (value) {
      if (!value) return '—'
      const parsed = moment(value)
      return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : '—'
    },
    versionType (version) {
      const snapshot = version.snapshot || {}
      return snapshot.type || '—'
    },
    versionName (version) {
      const snapshot = version.snapshot || {}
      return snapshot.name || '—'
    },
    truncate (text) {
      const value = String(text).trim()
      if (!value) return ''
      return value.length > SUMMARY_MAX_LENGTH
        ? `${value.slice(0, SUMMARY_MAX_LENGTH)}…`
        : value
    },
    // custom_html keeps its markup in `content`; every other type keeps the
    // copy inside `variables` (text / button label / link). Show whichever is
    // present, truncated, so the row stays scannable.
    versionSummary (version) {
      const snapshot = version.snapshot || {}

      if ((snapshot.type || '') === 'custom_html') {
        return this.truncate(snapshot.content || '') || '—'
      }

      const variables = snapshot.variables || {}
      for (const key of SUMMARY_VARIABLE_KEYS) {
        if (variables[key]) return this.truncate(variables[key])
      }

      return '—'
    },
    isArmed (version) {
      return this.confirmingId === version.id
    },
    // First click on Restore arms the row (button becomes "Confirm?"); a stray
    // leaving the button (blur) disarms it via resetConfirm.
    askConfirm (version) {
      this.confirmingId = version.id
    },
    resetConfirm () {
      this.confirmingId = null
    },
    restore (version) {
      if (this.restoringId !== null) return

      this.restoringId = version.id

      this.restoreVersion({ widgetId: this.widgetId, versionId: version.id })
        .then(() => {
          this.$toasted.success(this.$t('success_updated'))
          this.$emit('restored')
        })
        .catch(() => {
          this.$toasted.error(this.$t('error'))
        })
        .finally(() => {
          this.restoringId = null
          this.resetConfirm()
        })
    }
  }
}
